import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import './ProjectDetailsPage.css';

const ProjectDetailsPage = () => {
  const { id } = useParams();
  const { selectedCurrency } = useCurrency();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch project details
        const projectId = id || '1';
        console.log('[ProjectDetails] Fetching project:', projectId);
        const res = await fetch(`${API_BASE_URL}/projects/${projectId}`);
        
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to fetch project');
        }
        
        const data = await res.json();
        console.log('[ProjectDetails] Got data:', data);
          
        // Parse images JSON
        let images = [];
        try {
          images = data.images ? JSON.parse(data.images) : [];
        } catch (e) {
          images = [];
        }

        setProjectData({
          type: data.projectType || 'Project',
          title: data.title || 'Project Details',
          companyName: data.companyName || 'N/A',
          category: data.subCategory || data.projectType || 'N/A',
          quantity: data.budget ? `${data.currency || selectedCurrency.code} ${data.budget.toLocaleString()}` : 'N/A',
          area: data.city || data.country || 'N/A',
          description: data.description || '',
          mainImage: data.image || images[0] || '/uploads/builder-images/4b61fb9908c3fc0a62b18864aee8ce03d13e04fe_w2104.png',
          thumbnails: images.length > 0 ? images : [
            data.image || '/uploads/builder-images/aac384970d8208aa13ca1ff21eb67940d081ff3c_w395.png'
          ],
          extraImagesCount: Math.max(0, images.length - 5),
          country: data.country,
          city: data.city,
          startDate: data.startDate,
          endDate: data.endDate,
        });

        // Fetch related projects (only published)
        const relRes = await fetch(`${API_BASE_URL}/projects?limit=3&status=published`);
        if (relRes.ok) {
          const relData = await relRes.json();
          const currentId = data.id || parseInt(projectId);
          setRelatedProjects((relData.projects || []).filter(p => p.id !== currentId).slice(0, 3).map(p => ({
            id: p.id,
            slug: p.slug,
            image: p.image || '/uploads/builder-images/dfa323de11035416a213de014e57d4696d14b816_w568.png'
          })));
        }
      } catch (error) {
        console.error('Error fetching project:', error);
        setError(error.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="project-details-page">
        <Header />
        <main className="project-details-content" style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'50vh'}}>
          <p style={{color:'#666',fontSize:'18px'}}>Loading project details...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !projectData) {
    return (
      <div className="project-details-page">
        <Header />
        <main className="project-details-content" style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'50vh',flexDirection:'column',gap:'16px'}}>
          <p style={{color:'#d32f2f',fontSize:'18px'}}>{error || 'Project not found'}</p>
          <Link to="/projects" style={{color:'#1F396C',textDecoration:'underline'}}>Back to Projects</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const galleryImages = projectData.thumbnails.length > 0 ? projectData.thumbnails : [
    projectData.mainImage
  ];

  return (
    <div className="project-details-page">
      <Header />
      
      <main className="project-details-content">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Link to="/projects" className="breadcrumb-link">Project</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-current">Project Details</span>
        </div>

        <div className="project-details-wrapper">
          {/* Main Content */}
          <div className="project-main-content">

            {/* Project Header */}
            <div className="project-header">
              <div className="project-header-text">
                <span className="project-type-badge">{projectData.type}</span>
                <h1 className="project-type">{projectData.title}</h1>
                <div className="project-meta-row">
                  {projectData.city && (
                    <span className="project-meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {projectData.city}{projectData.country ? `, ${projectData.country}` : ''}
                    </span>
                  )}
                  {projectData.category && (
                    <span className="project-meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      {projectData.category}
                    </span>
                  )}
                </div>
              </div>
              <button 
                className="bookmark-btn-header"
                onClick={() => setIsBookmarked(!isBookmarked)}
                title={isBookmarked ? 'Remove bookmark' : 'Bookmark project'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill={isBookmarked ? "#1F396C" : "none"} stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                </svg>
                {isBookmarked ? 'Saved' : 'Save'}
              </button>
            </div>

            {/* Hero Image */}
            <div className="project-hero-image-wrap">
              <img 
                src={projectData.mainImage} 
                alt={projectData.title}
                className="project-hero-image"
              />
            </div>

            {/* Info Cards Grid */}
            <div className="project-info-grid">
              <div className="info-card">
                <span className="info-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <span className="info-card-label">Company</span>
                <span className="info-card-value company-highlight">{projectData.companyName}</span>
              </div>
              <div className="info-card">
                <span className="info-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                </span>
                <span className="info-card-label">Category</span>
                <span className="info-card-value">{projectData.category}</span>
              </div>
              <div className="info-card">
                <span className="info-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </span>
                <span className="info-card-label">Budget</span>
                <span className="info-card-value">{projectData.quantity}</span>
              </div>
              <div className="info-card">
                <span className="info-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </span>
                <span className="info-card-label">Area</span>
                <span className="info-card-value">{projectData.area}</span>
              </div>
              {projectData.startDate && (
                <div className="info-card">
                  <span className="info-card-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </span>
                  <span className="info-card-label">Start Date</span>
                  <span className="info-card-value">{new Date(projectData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              {projectData.endDate && (
                <div className="info-card">
                  <span className="info-card-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1585BC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </span>
                  <span className="info-card-label">End Date</span>
                  <span className="info-card-value">{new Date(projectData.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {projectData.description && (
              <div className="project-description-section">
                <h2 className="section-heading">About This Project</h2>
                <p className="project-description">{projectData.description}</p>
              </div>
            )}

            {/* Gallery */}
            {galleryImages.length > 0 && (
              <div className="project-gallery-section">
                <h2 className="section-heading">Project Gallery</h2>
                <div className="gallery-grid">
                  {galleryImages.map((img, index) => (
                    <div key={index} className="gallery-item">
                      <img src={img} alt={`Gallery ${index + 1}`} className="gallery-image" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Related Projects */}
          <aside className="project-sidebar">
            <h2 className="sidebar-title">Related Projects</h2>
            <div className="related-projects">
              {relatedProjects.map((project) => (
                <Link to={`/project/${project.id}`} key={project.id} className="related-project-card">
                  <img 
                    src={project.image} 
                    alt={`Related project ${project.id}`} 
                    className="related-project-image"
                  />
                  <div className="related-project-overlay">
                    <span className="related-view-label">View Project</span>
                  </div>
                </Link>
              ))}
              {relatedProjects.length === 0 && (
                <p className="no-related">No related projects found.</p>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectDetailsPage;
